import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { 
  Customer, 
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerResponse,
  CustomerUser,
  CustomerUserLinkRequest
} from '../../shared/models/admin';
import { ISelectOption } from '../../shared/models/select-option.interface';
@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.discoveryApiUrl}/Customers`;

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  // CRUD Básico
  getCustomers(): Observable<Customer[]> {
    return this.http.get<Customer[]>(this.baseUrl, { 
      headers: this.getHeaders() 
    }).pipe(
      catchError(this.handleError)
    );
  }

  getCustomerById(id: string): Observable<CustomerResponse> {
    return this.http.get<CustomerResponse>(`${this.baseUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createCustomer(customer: CreateCustomerRequest): Observable<CustomerResponse> {
    return this.http.post<CustomerResponse>(this.baseUrl, customer, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateCustomer(id: string, customer: any): Observable<CustomerResponse> {
    const updatePayload = { ...customer, id };
    return this.http.put<CustomerResponse>(`${this.baseUrl}`, updatePayload, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteCustomer(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Customer-User Relationships
  getCustomerUsers(customerId: string): Observable<CustomerUser[]> {
    return this.http.get<CustomerUser[]>(`${this.baseUrl}/${customerId}/Users`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  addCustomerUsers(customerId: string, userIds: string[]): Observable<void> {
    const payload: CustomerUserLinkRequest = { customerId, usersIds: userIds };
    return this.http.post<void>(`${this.baseUrl}/${customerId}/Users`, payload, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  removeCustomerUsers(customerId: string, userIds: string[]): Observable<void> {
    const payload: CustomerUserLinkRequest = { customerId, usersIds: userIds };
    return this.http.request<void>('delete', `${this.baseUrl}/${customerId}/Users`, {
      headers: this.getHeaders(),
      body: payload
    }).pipe(
      catchError(this.handleError)
    );
  }

  loadAvailableCustomersForSelection(): Observable<ISelectOption[]> {
    return this.getCustomers().pipe(
      map(customers => customers.map(customer => ({
        label: customer.name || customer.tenantName || 'Cliente Sem Nome',
        value: customer.id || '',
        integrationEnabled: customer.integrationEnabled === true,
        territorialIntelligenceEnabled:
          customer.territorialIntelligenceEnabled === true,
        environmentalLicensingReportEnabled:
          customer.environmentalLicensingReportEnabled === true
      })).filter(option => option.value)) // Filtrar clientes sem ID
    );
  }

  updateTerritorialIntelligence(
    customerId: string,
    enabled: boolean
  ): Observable<{ customerId: string; enabled: boolean }> {
    return this.http.put<{ customerId: string; enabled: boolean }>(
      `${environment.discoveryApiUrl}/customers/${customerId}/territorial-intelligence`,
      { enabled },
      { headers: this.getHeaders() }
    );
  }

  // Error handling
  private handleError = (error: any): Observable<never> => {
    console.error('CustomerService error:', error);
    
    let message = 'Erro inesperado no servidor';
    
    if (error.status) {
      switch (error.status) {
        case 400:
          message = 'Dados inválidos fornecidos';
          break;
        case 401:
          message = 'Sem autorização para esta operação';
          break;
        case 403:
          message = 'Acesso negado - apenas Owner pode gerenciar clientes';
          break;
        case 404:
          message = 'Cliente não encontrado';
          break;
        case 409:
          message = 'Nome do cliente já está em uso';
          break;
        case 422:
          message = 'Dados de validação incorretos';
          break;
        case 500:
          message = 'Erro interno do servidor';
          break;
      }
    }

    return throwError(() => new Error(message));
  };
}
